import { Component, OnDestroy, OnInit, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { CompanyService } from '../../../shared/services/company/company.service';

import { ToasterService } from '@purespectrum1/ui/toaster-service';
import {
  CompaniesResponse,
  CompanyResponseObject,
  SavedCombination,
  SingleSupplier,
} from './buyer-data-quality-settings.interface';

@Component({
  selector: 'ps-buyer-data-quality-settings',
  templateUrl: './buyer-data-quality-settings.component.html',
  styleUrls: ['./buyer-data-quality-settings.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuyerDataQualitySettingsComponent),
      multi: true,
    },
  ],
})
export class BuyerDataQualitySettingsComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  public buyerControl = new FormControl<number | null>(null, {
    validators: [Validators.required],
    nonNullable: false,
  });

  public suppliersControl = new FormControl<number[]>(
    { value: [], disabled: true },
    {
      validators: [Validators.required],
      nonNullable: true,
    }
  );

  public savedCombinations: SavedCombination[] = [];
  public buyers: CompanyResponseObject[] = [];
  public suppliers: SingleSupplier[] = [];
  public isEditing = false;

  private _buyerMap = new Map<number, string>();
  private _supplierMap = new Map<number, string>();

  private _subscriptions = new Subscription();
  // eslint-disable-next-line no-empty-function
  private _onChange: (value: SavedCombination[]) => void = () => {};
  // eslint-disable-next-line no-empty-function
  private _onTouched: () => void = () => {};

  constructor(
    private _companyService: CompanyService,
    private readonly _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this._getAllBuyers();
    this._getSuppliers();
    this._subscriptions.add(
      this.buyerControl.valueChanges.subscribe((buyerId) => {
        if (buyerId == null) {
          this.suppliersControl.disable({ emitEvent: false });
        } else {
          this.suppliersControl.enable({ emitEvent: false });
        }
      })
    );
  }

  // --- ControlValueAccessor Implementation ---

  public writeValue(value: SavedCombination[] | null | undefined): void {
    if (!Array.isArray(value)) {
      this.savedCombinations = [];
      return;
    }

    // Sanitize incoming array against null/undefined supplierIds or buyerIds
    this.savedCombinations = value.map((combo) => ({
      buyerId: Number(combo.buyerId),
      supplierIds: (combo.supplierIds ?? [])
        .map((id) => this._extractId(id))
        .filter((id): id is number => id != null),
    }));
  }

  public registerOnChange(fn: (value: SavedCombination[]) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  private _updateSelection(): void {
    this._onChange([...this.savedCombinations]);
    this._onTouched();
  }

  private _getAllBuyers(): void {
    const payload = { isSpplr: false, isBuyr: true };
    this._subscriptions.add(
      this._companyService
        .getAllBuyers(payload, true)
        .pipe(
          map(
            (resp: {
              companies?: Array<{ id: number | string; name: string }>;
            }) =>
              (resp.companies || []).map((company) => ({
                id: Number(company.id),
                name: company.name,
              }))
          )
        )
        .subscribe({
          next: (mappedBuyers) => {
            this.buyers = mappedBuyers;
            this._buyerMap = new Map(mappedBuyers.map((b) => [b.id, b.name]));
          },
          error: (error) => {
            this._toastr.error(error?.error?.msg || 'Error fetching buyers');
          },
        })
    );
  }

  private _getSuppliers(): void {
    this._subscriptions.add(
      this._companyService
        .getSupplierCompanies()
        .pipe(
          map((response: CompaniesResponse) =>
            (response.companies || []).map((supplier) => ({
              id: Number(supplier.id),
              name: supplier.name,
            }))
          )
        )
        .subscribe({
          next: (mappedSuppliers) => {
            this.suppliers = mappedSuppliers;
            this._supplierMap = new Map(
              mappedSuppliers.map((s) => [s.id, s.name])
            );
          },
          error: (error) => {
            this._toastr.error(error?.error?.msg || 'Error fetching suppliers');
          },
        })
    );
  }

  // --- Event Handlers ---

  public selectBuyer(event: any): void {
    if (!event) {
      this.clearSelection();
      return;
    }

    const rawBuyer = Array.isArray(event) ? event[0] : event;
    const buyerId = this._extractId(rawBuyer);

    if (buyerId == null) {
      this.clearSelection();
      return;
    }

    this.buyerControl.setValue(buyerId);

    const existingCombo = this.savedCombinations.find(
      (combo) => Number(combo.buyerId) === buyerId
    );

    if (existingCombo) {
      this.isEditing = true;
      const supplierIds = (existingCombo.supplierIds ?? [])
        .map((id) => this._extractId(id))
        .filter((id): id is number => id != null);

      this.suppliersControl.setValue(supplierIds);
    } else {
      this.isEditing = false;
      this.suppliersControl.setValue([]);
    }
  }

  public selectSuppliers(event: any): void {
    if (!event) {
      this.suppliersControl.setValue([]);
      return;
    }

    const rawList = Array.isArray(event) ? event : [event];
    const supplierIds = rawList
      .map((item) => this._extractId(item))
      .filter((id): id is number => id != null);

    this.suppliersControl.setValue(supplierIds);
  }

  public getBuyerName(id: number | string | null | undefined): string {
    const numId = this._extractId(id);
    return numId != null ? this._buyerMap.get(numId) || '' : '';
  }

  public getSupplierName(id: number | string | null | undefined): string {
    const numId = this._extractId(id);
    return numId != null ? this._supplierMap.get(numId) || '' : '';
  }

  public getSelectedSuppliersLabel(): string {
    const selectedIds = this.suppliersControl.value || [];

    if (selectedIds.length === 0) {
      return 'Select Suppliers';
    }
    if (selectedIds.length === 1) {
      return this.getSupplierName(selectedIds[0]);
    }

    return `${selectedIds.length} Suppliers Selected`;
  }

  public addCombination(): void {
    const currentBuyerId = this._extractId(this.buyerControl.value);
    const currentSupplierIds = (this.suppliersControl.value ?? [])
      .map((id) => this._extractId(id))
      .filter((id): id is number => id != null);

    if (currentBuyerId == null || currentSupplierIds.length === 0) {
      return;
    }

    const existingIndex = this.savedCombinations.findIndex(
      (combo) => Number(combo.buyerId) === currentBuyerId
    );

    const buyerName = this.getBuyerName(currentBuyerId);

    const newCombo: SavedCombination = {
      buyerId: currentBuyerId,
      supplierIds: currentSupplierIds,
    };

    if (existingIndex > -1) {
      this.savedCombinations[existingIndex] = newCombo;
      this._toastr.success(`Updated entry for ${buyerName}`);
    } else {
      this.savedCombinations.push(newCombo);
      this._toastr.success(`Added entry for ${buyerName}`);
    }

    this._updateSelection();
    this.clearSelection();
  }

  public removeCombination(index: number): void {
    const removedCombo = this.savedCombinations[index];
    this.savedCombinations.splice(index, 1);

    const activeBuyerId = this._extractId(this.buyerControl.value);
    if (
      activeBuyerId != null &&
      activeBuyerId === Number(removedCombo.buyerId)
    ) {
      this.clearSelection();
    } else {
      this._updateSelection();
    }
  }

  public clearSelection(): void {
    this.isEditing = false;
    this.buyerControl.setValue(null);
    this.suppliersControl.setValue([]);
  }

  public trackByBuyerId(_index: number, item: SavedCombination): number {
    return Number(item.buyerId);
  }

  private _extractId(val: any): number | null {
    if (val == null) {
      return null;
    }
    if (typeof val === 'object') {
      return val.id != null ? Number(val.id) : null;
    }
    const num = Number(val);
    return Number.isNaN(num) ? null : num;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
